import React from "react";
import { cn } from '@/lib/utils'
import type { ChatMessage } from '@/hooks/use-realtime-chat'
import { Button } from '@/components/ui/button'
import { SmilePlus } from 'lucide-react'

interface ChatMessageItemProps {
  message: ChatMessage
  isOwnMessage: boolean
  showHeader: boolean
  onAddReaction?: (messageId: string, emoji: string) => void
}

export const ChatMessageItem = ({ 
  message, 
  isOwnMessage, 
  showHeader,
  onAddReaction
}: ChatMessageItemProps) => {
  return (
    <div className={`flex mt-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div
        className={cn('max-w-[85%] w-fit flex flex-col gap-1', {
          'items-end': isOwnMessage,
        })}
      >
        {showHeader && (
          <div
            className={cn('flex items-center gap-2 text-xs px-3', {
              'justify-end flex-row-reverse': isOwnMessage,
            })}
          >
            <span className={'font-medium'}>{message.user.name}</span>
            <span className="text-foreground/50 text-xs">
              {new Date(message.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
              {message.is_edited && ' (edited)'}
            </span>
          </div>
        )}
        
        {/* Quoted message */}
        {message.quotedMessage && (
          <div className="border-l-4 border-gray-300 pl-2 ml-1 italic text-gray-500 text-sm">
            {message.quotedMessage.sender.name}: {message.quotedMessage.content}
          </div>
        )}
        
        {/* Message content */}
        <div
          className={cn(
            'py-2 px-3 rounded-xl text-sm w-fit relative group',
            isOwnMessage ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
          )}
        >
          {message.content}
          
          {/* Attachment previews */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {message.attachments.map((attachment, index) => (
                <div key={index} className="max-w-[100px]">
                  {attachment.type === 'image' && (
                    <img 
                      src={attachment.thumbnailUrl || attachment.url} 
                      alt="Attachment" 
                      className="rounded-lg max-w-full max-h-[100px]"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Reaction button */}
          {!isOwnMessage && onAddReaction && (
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute -bottom-3 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onAddReaction(message.id, '👍')}
            >
              <SmilePlus size={16} />
            </Button>
          )}
        </div>
        
        {/* Reactions */}
        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.values(message.reactions).map((reaction, index) => (
              <div 
                key={index} 
                className="bg-background px-2 py-1 rounded-full text-xs flex items-center gap-1"
              >
                <span>{reaction.emoji}</span>
                <span>{reaction.users.length}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
